import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiZap, FiSettings, FiMessageSquare, FiCloud, FiPackage, FiCheck, FiArrowRight, FiBrain, FiTrendingUp, FiDatabase } = FiIcons;

const Services = () => {
  const [headerRef, headerInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [servicesRef, servicesInView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const services = [
    {
      icon: FiBrain,
      title: 'AI-Powered Application Development',
      description: 'End-to-end intelligent applications with advanced machine learning capabilities',
      features: [
        'Custom AI model training and deployment',
        'Real-time inference pipelines with monitoring',
        'React/Next.js frontend with intelligent UX',
        'Multi-modal AI integration (text, image, voice)',
        'Scalable cloud architecture with auto-scaling'
      ],
      deliverables: [
        'Production-ready AI application (3-4 weeks)',
        'Comprehensive testing and validation suite',
        'Deployment automation and monitoring dashboard'
      ]
    },
    {
      icon: FiSettings,
      title: 'Intelligent Process Automation',
      description: 'Transform manual workflows into smart, self-optimizing systems',
      features: [
        'Business process analysis and optimization',
        'AI-driven decision engines and rule automation',
        'Integration with existing enterprise systems',
        'Continuous learning and process improvement',
        'Comprehensive audit trails and compliance reporting'
      ],
      deliverables: [
        'Automated workflow implementation',
        'Performance monitoring and analytics dashboard',
        'Staff training and change management support'
      ]
    },
    {
      icon: FiMessageSquare,
      title: 'Conversational AI & Chatbots',
      description: 'Advanced conversational interfaces with natural language understanding',
      features: [
        'Multi-language support with cultural adaptation',
        'Context-aware conversations with memory',
        'Integration with knowledge bases and CRM systems',
        'Sentiment analysis and emotion detection',
        'Voice and text interaction capabilities'
      ],
      deliverables: [
        'Fully integrated conversational AI system',
        'Performance analytics and optimization reports',
        'Admin interface for content management'
      ]
    },
    {
      icon: FiTrendingUp,
      title: 'Predictive Analytics & Insights',
      description: 'Transform data into actionable intelligence with advanced analytics',
      features: [
        'Custom predictive models for business forecasting',
        'Real-time data processing and visualization',
        'Anomaly detection and alert systems',
        'A/B testing frameworks and statistical analysis',
        'Executive dashboards with KPI tracking'
      ],
      deliverables: [
        'Interactive analytics platform',
        'Predictive model deployment',
        'Training and documentation package'
      ]
    },
    {
      icon: FiCloud,
      title: 'Cloud Infrastructure & Integration',
      description: 'Scalable, secure cloud solutions with enterprise-grade reliability',
      features: [
        'Multi-cloud deployment strategies (AWS, Azure, GCP)',
        'Microservices architecture with container orchestration',
        'API gateway and service mesh implementation',
        'Advanced security and compliance frameworks',
        'Disaster recovery and business continuity planning'
      ],
      deliverables: [
        'Production cloud infrastructure',
        'Security and compliance documentation',
        'Operational runbooks and monitoring setup'
      ]
    },
    {
      icon: FiPackage,
      title: 'Enterprise AI Solutions',
      description: 'White-label platforms and custom enterprise integrations',
      features: [
        'Custom branding and multi-tenant architecture',
        'Advanced user management and permissions',
        'API-first design for seamless integration',
        'Comprehensive analytics and usage tracking',
        'Dedicated support and maintenance programs'
      ],
      deliverables: [
        'Fully branded enterprise platform',
        'Integration and onboarding systems',
        'Ongoing support and feature development'
      ]
    }
  ];

  const pricingModels = [
    {
      title: 'Rapid Prototyping',
      description: 'Fast validation sprints',
      features: ['1-2 week development cycles', 'MVP delivery and testing', 'Proof of concept validation'],
      highlight: 'Perfect for innovation teams'
    },
    {
      title: 'Production Deployment',
      description: 'Full-scale implementation',
      features: ['Enterprise-grade architecture', 'Complete testing and QA', 'Production deployment support'],
      highlight: 'Comprehensive business solutions'
    },
    {
      title: 'Managed Services',
      description: 'Ongoing optimization',
      features: ['24/7 monitoring and support', 'Continuous improvement', 'Regular feature updates'],
      highlight: 'Hands-off operation'
    },
    {
      title: 'Enterprise Partnership',
      description: 'Strategic collaboration',
      features: ['Co-development opportunities', 'Revenue sharing models', 'Priority feature development'],
      highlight: 'Long-term growth partnership'
    }
  ];

  return (
    <div className="relative overflow-hidden">
      {/* Background Elements */}
      <div className="floating-orb floating-orb-1"></div>
      <div className="floating-orb floating-orb-2"></div>

      {/* Header */}
      <section ref={headerRef} className="py-24 lg:py-32 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={headerInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-6xl sm:text-7xl lg:text-8xl font-bold mb-8">
              <span className="bg-gradient-to-r from-white via-gray-200 to-gray-400 bg-clip-text text-transparent">
                Our Services
              </span>
            </h1>
            <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
              Comprehensive AI solutions designed to transform your business operations, 
              enhance customer experiences, and drive measurable growth through intelligent automation.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services */}
      <section ref={servicesRef} className="py-20 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-20">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={servicesInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"
              >
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="inline-flex items-center justify-center w-18 h-18 bg-gradient-to-r from-primary-500 to-accent-purple rounded-2xl mb-6 shadow-lg glass-button">
                    <SafeIcon icon={service.icon} className="w-10 h-10 text-white" />
                  </div>
                  <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">{service.title}</h2>
                  <p className="text-lg text-gray-300 mb-8">{service.description}</p>
                  
                  <div className="space-y-6">
                    <h3 className="text-xl font-semibold text-white">Core Capabilities:</h3>
                    <ul className="space-y-4">
                      {service.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-start">
                          <SafeIcon icon={FiCheck} className="w-6 h-6 text-primary-400 mr-3 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-300">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                  <div className="glass-strong rounded-3xl p-8">
                    <h3 className="text-xl font-semibold text-white mb-6 flex items-center">
                      <SafeIcon icon={FiArrowRight} className="w-6 h-6 text-primary-400 mr-3" />
                      What You Get
                    </h3>
                    <ul className="space-y-4">
                      {service.deliverables.map((deliverable, deliverableIndex) => (
                        <li key={deliverableIndex} className="flex items-start">
                          <div className="w-2 h-2 bg-primary-400 rounded-full mr-4 mt-3"></div>
                          <span className="text-gray-300">{deliverable}</span>
                        </li>
                      ))}
                    </ul>

                    <div className="mt-8 p-4 glass-card rounded-2xl">
                      <h4 className="font-semibold text-primary-300 mb-2">Typical Timeline</h4>
                      <div className="flex items-center text-primary-200">
                        <SafeIcon icon={FiZap} className="w-4 h-4 mr-2" />
                        <span className="text-sm">2-6 weeks from concept to deployment</span>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Enhanced Pricing Models */}
      <section className="py-20 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-5xl lg:text-6xl font-bold mb-6">
              <span className="bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                Flexible Engagement Models
              </span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Choose the engagement model that best fits your timeline, budget, and business objectives
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {pricingModels.map((model, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                whileHover={{ y: -8, scale: 1.02 }}
                className="glass-card rounded-2xl p-6 transition-all duration-500 relative overflow-hidden"
              >
                <div className="relative z-10">
                  <h3 className="text-xl font-semibold text-white mb-2">{model.title}</h3>
                  <p className="text-primary-400 font-medium mb-4">{model.description}</p>
                  
                  <ul className="space-y-3 mb-6">
                    {model.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center text-sm text-gray-300">
                        <SafeIcon icon={FiCheck} className="w-4 h-4 text-primary-400 mr-2" />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  <div className="glass-button px-3 py-2 rounded-lg text-xs font-medium text-primary-300">
                    {model.highlight}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-16">
            <div className="glass-strong rounded-3xl p-8 max-w-4xl mx-auto">
              <p className="text-gray-300 mb-6 text-lg">
                All engagements include comprehensive discovery, security assessment, and scalability planning 
                to ensure your solution grows with your business needs.
              </p>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <a
                  href="/contact"
                  className="inline-flex items-center px-10 py-5 bg-gradient-to-r from-primary-500 to-accent-purple text-white rounded-2xl font-semibold hover:shadow-2xl hover:shadow-primary-500/25 transition-all duration-300 glass-button"
                >
                  <SafeIcon icon={FiMessageSquare} className="w-6 h-6 mr-3" />
                  Discuss Your Project
                </a>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;